import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ReceiptLongRounded as LedgerIcon,
  SearchRounded as SearchIcon,
  RefreshRounded as RefreshIcon,
  AccountBalanceWalletRounded as WalletIcon,
  PaidRounded as PaidIcon,
  PriceCheckRounded as FeeIcon,
  TuneRounded as SettingsIcon,
  CheckCircleRounded as CheckIcon,
  SwapHorizRounded as TransferIcon,
  PercentRounded as PercentIcon,
  ShieldRounded as ShieldIcon,
  SavingsRounded as SavingsIcon,
  HelpOutlineRounded as HelpIcon,
  ArrowForwardRounded as ArrowIcon,
} from "@mui/icons-material";
import API from "../../api/api";
import { S } from "../../theme/tokens";

function fmtAmount(value) {
  const num = Number(value || 0);
  return Number.isFinite(num) ? num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00";
}

export default function AdminTotalAdminCharges() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ summary: {}, results: [] });
  const [search, setSearch] = useState("");
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  // Platform Tax & Deductions Configuration
  const [taxConfig, setTaxConfig] = useState({
    p2p_package_tax_percent: 7,
    self_package_tax_percent: 7,
    p2p_coupon_tax_percent: 7,
    withdrawal_tax_percent: 10,
  });
  const [taxSaving, setTaxSaving] = useState(false);
  const [taxMsg, setTaxMsg] = useState({ type: "", text: "" });

  const fetchTaxConfig = async () => {
    try {
      const res = await API.get("/business/platform-taxes/");
      if (res.data) {
        setTaxConfig({
          p2p_package_tax_percent: res.data.p2p_package_tax_percent ?? 7,
          self_package_tax_percent: res.data.self_package_tax_percent ?? 7,
          p2p_coupon_tax_percent: res.data.p2p_coupon_tax_percent ?? 7,
          withdrawal_tax_percent: res.data.withdrawal_tax_percent ?? 10,
        });
      }
    } catch (e) {
      console.error("Failed to load platform taxes:", e);
    }
  };

  const saveTaxConfig = async () => {
    try {
      setTaxSaving(true);
      setTaxMsg({ type: "", text: "" });
      const res = await API.post("/business/platform-taxes/", taxConfig);
      setTaxMsg({ type: "success", text: "Platform tax and admin charge rates saved successfully!" });
      setTimeout(() => {
        setTaxMsg({ type: "", text: "" });
      }, 5000);
    } catch (err) {
      setTaxMsg({ type: "error", text: err?.response?.data?.detail || "Failed to update tax rates." });
    } finally {
      setTaxSaving(false);
    }
  };

  const fetchCharges = async () => {
    try {
      setLoading(true);
      const res = await API.get("/business/admin/total-admin-charges/");
      setData(res.data || { summary: {}, results: [] });
      setLastRefreshed(new Date());
    } catch (err) {
      console.error("Failed to fetch admin charges:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCharges();
    fetchTaxConfig();
  }, []);

  const summary = data.summary || {};
  const results = data.results || [];

  const filteredResults = useMemo(() => {
    if (!search.trim()) return results;
    const q = search.toLowerCase();
    return results.filter((item) => {
      return (
        String(item.user_phone || "").toLowerCase().includes(q) ||
        String(item.charge_type || "").toLowerCase().includes(q) ||
        String(item.raw_type || "").toLowerCase().includes(q) ||
        String(item.id || "").includes(q)
      );
    });
  }, [results, search]);

  const getCategoryBadge = (chargeType, rawType) => {
    const text = String(chargeType || "");
    const raw = String(rawType || "");
    if (text.includes("Self") || raw.includes("SELF")) {
      return { label: "Self Transfer", bg: "#ECFDF5", color: "#059669", border: "#A7F3D0" };
    }
    if (text.includes("P2P") || raw.includes("P2P")) {
      return { label: "Member Transfer", bg: "#EFF6FF", color: "#2563EB", border: "#BFDBFE" };
    }
    if (text.includes("Activation") || raw.includes("ACTIVATION")) {
      return { label: "Package Activation", bg: "#FEF3C7", color: "#D97706", border: "#FDE68A" };
    }
    return { label: "TDS / Admin Withholding", bg: "#F3E8FF", color: "#7C3AED", border: "#DDD6FE" };
  };

  return (
    <Box sx={{ maxWidth: 1360, mx: "auto", p: { xs: 1.5, sm: 2.5, md: 3.5 } }}>
      {/* Hero Header Banner */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 4,
          background: "linear-gradient(135deg, #090D16 0%, #111827 50%, #1E293B 100%)",
          color: "#fff",
          mb: 3,
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 20px 25px -5px rgba(15, 23, 42, 0.15), 0 8px 10px -6px rgba(15, 23, 42, 0.1)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2.5}
        >
          <Box sx={{ maxWidth: 720 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 44,
                  height: 44,
                  borderRadius: 3,
                  bgcolor: "rgba(56, 189, 248, 0.15)",
                  border: "1px solid rgba(56, 189, 248, 0.3)",
                  color: "#38BDF8",
                }}
              >
                <LedgerIcon sx={{ fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900, color: "#fff", letterSpacing: -0.5, fontSize: { xs: "20px", sm: "24px" } }}>
                  Total Admin Charges Ledger
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                  <Chip
                    size="small"
                    icon={<ShieldIcon sx={{ fontSize: "14px !important", color: "#38BDF8 !important" }} />}
                    label="Live Platform Audit"
                    sx={{
                      bgcolor: "rgba(56, 189, 248, 0.12)",
                      color: "#7DD3FC",
                      fontWeight: 700,
                      fontSize: "11px",
                      height: 22,
                    }}
                  />
                  <Typography sx={{ color: "#64748B", fontSize: "12px", fontWeight: 500 }}>
                    Refreshed {lastRefreshed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
            <Typography variant="body2" sx={{ color: "#94A3B8", fontWeight: 400, fontSize: "13.5px", lineHeight: 1.6 }}>
              Centralized revenue auditing for all platform deduction streams: Gift Voucher Member Transfers, Self Conversions, Package Activations, and Withdrawal TDS.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: { xs: "100%", md: "auto" }, justifyContent: { xs: "flex-end", md: "flex-end" } }}>
            <Button
              variant="outlined"
              onClick={fetchCharges}
              disabled={loading}
              startIcon={<RefreshIcon sx={{ animation: loading ? "spin 1s linear infinite" : "none" }} />}
              sx={{
                bgcolor: "rgba(255,255,255,0.06)",
                borderColor: "rgba(255,255,255,0.15)",
                color: "#F1F5F9",
                fontWeight: 700,
                textTransform: "none",
                borderRadius: 2.5,
                px: 2,
                py: 1,
                fontSize: "13px",
                "&:hover": { bgcolor: "rgba(255,255,255,0.12)", borderColor: "rgba(255,255,255,0.25)" },
              }}
            >
              {loading ? "Refreshing..." : "Refresh Ledger"}
            </Button>
          </Stack>
        </Stack>

        {/* 4-Metric Grid */}
        <Grid container spacing={2} sx={{ mt: 2.5 }}>
          <Grid item xs={6} md={3}>
            <Box
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                backdropFilter: "blur(8px)",
                transition: "transform 0.2s, border-color 0.2s",
                "&:hover": { transform: "translateY(-2px)", borderColor: "rgba(56, 189, 248, 0.4)" },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: "11px", fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Total Collected
                </Typography>
                <PaidIcon sx={{ color: "#38BDF8", fontSize: 20, opacity: 0.8 }} />
              </Stack>
              <Typography sx={{ fontSize: { xs: "20px", sm: "24px" }, fontWeight: 900, color: "#38BDF8", letterSpacing: -0.5 }}>
                ₹{fmtAmount(summary.total_admin_charges)}
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "#64748B", mt: 0.5 }}>
                All revenue deductions combined
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={3}>
            <Box
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                backdropFilter: "blur(8px)",
                transition: "transform 0.2s, border-color 0.2s",
                "&:hover": { transform: "translateY(-2px)", borderColor: "rgba(74, 222, 128, 0.4)" },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: "11px", fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Voucher & P2P Fees
                </Typography>
                <TransferIcon sx={{ color: "#4ADE80", fontSize: 20, opacity: 0.8 }} />
              </Stack>
              <Typography sx={{ fontSize: { xs: "20px", sm: "24px" }, fontWeight: 900, color: "#4ADE80", letterSpacing: -0.5 }}>
                ₹{fmtAmount(summary.total_p2p_fees)}
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "#64748B", mt: 0.5 }}>
                Member & Self transfers
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={3}>
            <Box
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                backdropFilter: "blur(8px)",
                transition: "transform 0.2s, border-color 0.2s",
                "&:hover": { transform: "translateY(-2px)", borderColor: "rgba(251, 191, 36, 0.4)" },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: "11px", fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Activation Fees
                </Typography>
                <WalletIcon sx={{ color: "#FBBF24", fontSize: 20, opacity: 0.8 }} />
              </Stack>
              <Typography sx={{ fontSize: { xs: "20px", sm: "24px" }, fontWeight: 900, color: "#FBBF24", letterSpacing: -0.5 }}>
                ₹{fmtAmount(summary.total_activation_fees)}
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "#64748B", mt: 0.5 }}>
                Digital package onboarding
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={3}>
            <Box
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                backdropFilter: "blur(8px)",
                transition: "transform 0.2s, border-color 0.2s",
                "&:hover": { transform: "translateY(-2px)", borderColor: "rgba(192, 132, 252, 0.4)" },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: "11px", fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Withdrawal TDS & Tax
                </Typography>
                <FeeIcon sx={{ color: "#C084FC", fontSize: 20, opacity: 0.8 }} />
              </Stack>
              <Typography sx={{ fontSize: { xs: "20px", sm: "24px" }, fontWeight: 900, color: "#C084FC", letterSpacing: -0.5 }}>
                ₹{fmtAmount(summary.total_withdrawal_admin_fees)}
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "#64748B", mt: 0.5 }}>
                Bank payout withholdings
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Platform Taxes & Charges Configuration Card */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3.5,
          border: "1px solid #E2E8F0",
          bgcolor: "#FFFFFF",
          boxShadow: S.card,
          mb: 3,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 2.5 }}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  borderRadius: 2.5,
                  bgcolor: "#EFF6FF",
                  color: "#2563EB",
                }}
              >
                <SettingsIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#0F172A", fontSize: "17px" }}>
                  Platform Taxes & Charges Configuration
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748B", fontSize: "13px" }}>
                  100% Dynamic Commission Engine fee percentages applied across voucher transfers and withdrawals
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Button
            variant="contained"
            disabled={taxSaving}
            onClick={saveTaxConfig}
            startIcon={taxSaving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
            sx={{
              bgcolor: "#2563EB",
              color: "#fff",
              fontWeight: 700,
              borderRadius: 2.5,
              px: 3,
              py: 1.1,
              fontSize: "13.5px",
              textTransform: "none",
              boxShadow: "0 4px 6px -1px rgba(37, 99, 235, 0.25)",
              "&:hover": { bgcolor: "#1D4ED8" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {taxSaving ? "Saving..." : "Save Tax Configuration"}
          </Button>
        </Stack>

        {taxMsg.text && (
          <Alert severity={taxMsg.type} sx={{ mb: 2.5, borderRadius: 2.5 }} onClose={() => setTaxMsg({ type: "", text: "" })}>
            {taxMsg.text}
          </Alert>
        )}

        {/* Section 1: Gift Voucher Fees (Primary Consumer Flows) */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Chip
              size="small"
              label="Primary Consumer Fees"
              sx={{ bgcolor: "#F1F5F9", color: "#334155", fontWeight: 700, fontSize: "11px", height: 22 }}
            />
            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#475569" }}>
              Gift Voucher Transfer Fees
            </Typography>
          </Stack>

          <Grid container spacing={2}>
            {/* Card 1: Send to Member */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  bgcolor: "#F8FAFC",
                  border: "1.5px solid #E2E8F0",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  "&:focus-within": { borderColor: "#2563EB", bgcolor: "#fff", boxShadow: "0 4px 12px rgba(37, 99, 235, 0.08)" },
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 800, color: "#0F172A" }}>
                    Gift Voucher → Send to Member Fee (%)
                  </Typography>
                  <Chip size="small" label="P2P Transfer" sx={{ bgcolor: "#EFF6FF", color: "#2563EB", fontWeight: 700, fontSize: "11px" }} />
                </Stack>
                <Typography sx={{ fontSize: "12px", color: "#64748B", mb: 2, minHeight: 36, lineHeight: 1.5 }}>
                  Platform fee deducted when a consumer sends a digital Gift Voucher to another registered member.
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  fullWidth
                  value={taxConfig.p2p_package_tax_percent}
                  onChange={(e) => setTaxConfig({ ...taxConfig, p2p_package_tax_percent: e.target.value })}
                  inputProps={{ min: 0, max: 100, step: 0.5 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end"><Typography sx={{ fontWeight: 800, color: "#2563EB" }}>%</Typography></InputAdornment>,
                    sx: { bgcolor: "#fff", fontWeight: 800, fontSize: "15px", borderRadius: 2 },
                  }}
                />
              </Box>
            </Grid>

            {/* Card 2: Self Transfer */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  bgcolor: "#F8FAFC",
                  border: "1.5px solid #E2E8F0",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  "&:focus-within": { borderColor: "#059669", bgcolor: "#fff", boxShadow: "0 4px 12px rgba(5, 150, 105, 0.08)" },
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 800, color: "#0F172A" }}>
                    Gift Voucher → Self Transfer Fee (%)
                  </Typography>
                  <Chip size="small" label="Self Conversion" sx={{ bgcolor: "#ECFDF5", color: "#059669", fontWeight: 700, fontSize: "11px" }} />
                </Stack>
                <Typography sx={{ fontSize: "12px", color: "#64748B", mb: 2, minHeight: 36, lineHeight: 1.5 }}>
                  Admin fee deducted when converting wallet balance to a Self Package Voucher for personal upgrade/rebirth.
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  fullWidth
                  value={taxConfig.self_package_tax_percent}
                  onChange={(e) => setTaxConfig({ ...taxConfig, self_package_tax_percent: e.target.value })}
                  inputProps={{ min: 0, max: 100, step: 0.5 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end"><Typography sx={{ fontWeight: 800, color: "#059669" }}>%</Typography></InputAdornment>,
                    sx: { bgcolor: "#fff", fontWeight: 800, fontSize: "15px", borderRadius: 2 },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2.5, borderColor: "#F1F5F9" }} />

        {/* Section 2: Withdrawal TDS & Internal Pockets */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Chip
              size="small"
              label="Secondary Deductions"
              sx={{ bgcolor: "#F1F5F9", color: "#334155", fontWeight: 700, fontSize: "11px", height: 22 }}
            />
            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#475569" }}>
              Withdrawal TDS & Internal Balance Transfers
            </Typography>
          </Stack>

          <Grid container spacing={2}>
            {/* Card 3: Withdrawal TDS */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  bgcolor: "#F8FAFC",
                  border: "1.5px solid #E2E8F0",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  "&:focus-within": { borderColor: "#7C3AED", bgcolor: "#fff", boxShadow: "0 4px 12px rgba(124, 58, 237, 0.08)" },
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 800, color: "#0F172A" }}>
                    Withdrawal TDS / Tax (%)
                  </Typography>
                  <Tooltip title="This setting is synchronized with the Withdrawal Control Center (asiyapp.com/admin/withdrawals)">
                    <Chip size="small" label="Synced with Withdrawals" sx={{ bgcolor: "#F3E8FF", color: "#7C3AED", fontWeight: 700, fontSize: "11px" }} />
                  </Tooltip>
                </Stack>
                <Typography sx={{ fontSize: "12px", color: "#64748B", mb: 2, minHeight: 36, lineHeight: 1.5 }}>
                  Statutory TDS / Admin fee withheld whenever a member transfers to Withdrawal Pocket or requests a bank payout.
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  fullWidth
                  value={taxConfig.withdrawal_tax_percent}
                  onChange={(e) => setTaxConfig({ ...taxConfig, withdrawal_tax_percent: e.target.value })}
                  inputProps={{ min: 0, max: 100, step: 0.5 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end"><Typography sx={{ fontWeight: 800, color: "#7C3AED" }}>%</Typography></InputAdornment>,
                    sx: { bgcolor: "#fff", fontWeight: 800, fontSize: "15px", borderRadius: 2 },
                  }}
                />
              </Box>
            </Grid>

            {/* Card 4: Coupon Pocket Transfer */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  bgcolor: "#F8FAFC",
                  border: "1.5px solid #E2E8F0",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  "&:focus-within": { borderColor: "#64748B", bgcolor: "#fff", boxShadow: "0 4px 12px rgba(100, 116, 139, 0.08)" },
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 800, color: "#0F172A" }}>
                    Internal Coupon Pocket Fee (%)
                  </Typography>
                  <Chip size="small" label="Internal Pocket" sx={{ bgcolor: "#F1F5F9", color: "#475569", fontWeight: 700, fontSize: "11px" }} />
                </Stack>
                <Typography sx={{ fontSize: "12px", color: "#64748B", mb: 2, minHeight: 36, lineHeight: 1.5 }}>
                  Fee applied if transferring balance from Main Wallet into internal Coupon Pocket balance.
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  fullWidth
                  value={taxConfig.p2p_coupon_tax_percent}
                  onChange={(e) => setTaxConfig({ ...taxConfig, p2p_coupon_tax_percent: e.target.value })}
                  inputProps={{ min: 0, max: 100, step: 0.5 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end"><Typography sx={{ fontWeight: 800, color: "#64748B" }}>%</Typography></InputAdornment>,
                    sx: { bgcolor: "#fff", fontWeight: 800, fontSize: "15px", borderRadius: 2 },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Search & Audit Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          border: "1px solid #E2E8F0",
          bgcolor: "#FFFFFF",
          boxShadow: S.card,
          mb: 2.5,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1.5,
        }}
      >
        <TextField
          size="small"
          placeholder="Search by User Phone, Category, or TX ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: "100%", sm: 360 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#94A3B8", fontSize: 20 }} />
              </InputAdornment>
            ),
            sx: { borderRadius: 2 },
          }}
        />

        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#64748B" }}>
            Showing <strong style={{ color: "#0F172A" }}>{filteredResults.length}</strong> of {results.length} transactions
          </Typography>
        </Stack>
      </Paper>

      {/* Ledger Table Container */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3.5,
          border: "1px solid #E2E8F0",
          bgcolor: "#FFFFFF",
          boxShadow: S.card,
          overflow: "hidden",
        }}
      >
        <TableContainer sx={{ maxHeight: 680, width: "100%", overflowX: "auto" }}>
          <Table stickyHeader size={isMobile ? "small" : "medium"} sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow sx={{ "& th": { bgcolor: "#F8FAFC", borderBottom: "1.5px solid #E2E8F0" } }}>
                <TableCell sx={{ fontWeight: 800, color: "#475569", fontSize: "12px", width: 90 }}>TX ID</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#475569", fontSize: "12px", width: 150 }}>SOURCE USER</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#475569", fontSize: "12px", width: 180 }}>CHARGE CATEGORY</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#475569", fontSize: "12px", textAlign: "right", width: 140 }}>GROSS AMOUNT</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#475569", fontSize: "12px", textAlign: "right", width: 140 }}>FEE COLLECTED</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#475569", fontSize: "12px", textAlign: "right", width: 180 }}>DATE & TIME</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", py: 8 }}>
                    <CircularProgress size={36} sx={{ color: "#2563EB" }} />
                    <Typography sx={{ fontSize: "13.5px", color: "#64748B", fontWeight: 600, mt: 1.5 }}>
                      Loading platform audit records...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredResults.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", py: 8 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 3,
                        bgcolor: "#F1F5F9",
                        color: "#94A3B8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 1.5,
                      }}
                    >
                      <FeeIcon sx={{ fontSize: 32 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 800, color: "#334155", fontSize: "15px" }}>No admin charges recorded</Typography>
                    <Typography sx={{ fontSize: "13px", color: "#64748B", mt: 0.5 }}>
                      Transactions with admin fees or withholdings will automatically appear here.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredResults.map((item) => {
                  const badge = getCategoryBadge(item.charge_type, item.raw_type);
                  return (
                    <TableRow
                      key={item.id}
                      hover
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        "&:hover": { bgcolor: "#F8FAFC" },
                        transition: "background-color 0.15s",
                      }}
                    >
                      <TableCell sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: "12.5px", color: "#64748B" }}>
                        #{item.id}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: "13px", color: "#0F172A" }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: 26,
                              height: 26,
                              borderRadius: "50%",
                              bgcolor: "#F1F5F9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "11px",
                              fontWeight: 800,
                              color: "#475569",
                            }}
                          >
                            {String(item.user_phone || "U").slice(-2)}
                          </Box>
                          <Typography sx={{ fontSize: "13px", fontWeight: 800, color: "#0F172A" }}>
                            {item.user_phone}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={badge.label}
                          sx={{
                            fontWeight: 700,
                            fontSize: "11.5px",
                            bgcolor: badge.bg,
                            color: badge.color,
                            border: `1px solid ${badge.border}`,
                            height: 24,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#475569", textAlign: "right" }}>
                        ₹{fmtAmount(item.gross_amount)}
                      </TableCell>
                      <TableCell sx={{ textAlign: "right" }}>
                        <Typography sx={{ fontSize: "13.5px", fontWeight: 900, color: "#16A34A" }}>
                          + ₹{fmtAmount(item.amount)}
                        </Typography>
                        {Number(item.fee_percent) > 0 && (
                          <Typography sx={{ fontSize: "11px", color: "#64748B", fontWeight: 600 }}>
                            ({item.fee_percent}%)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px", color: "#64748B", textAlign: "right", whiteSpace: "nowrap" }}>
                        {item.date}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
